import { blinkConstants } from '@thoughtspot/blink-constants';
import { PageContexts, PageType, setPageContext, useEmbedContext, } from '@thoughtspot/blink-context';
import { useTranslation } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect } from 'react';
import ReactModal from 'react-modal';
import { Alert } from '../alert';
import { Button } from '../button';
import { IconID } from '../icon';
import { LoadingIndicator } from '../loading-indicator';
import { ModalSize } from '../modal';
import { ProgressBarColor } from '../progress-bar';
import { Typography } from '../typography';
import styles from './wizard-modal.module.scss';
const logger = create('wizard-modal');
ReactModal.setAppElement('body');
ReactModal.defaultStyles.overlay.backgroundColor = 'rgba(28,35,48,.5)';
export var WizardModalTestId;
(function (WizardModalTestId) {
    WizardModalTestId["CONTAINER"] = "wizard-modal-container";
    WizardModalTestId["PROGRESSBAR"] = "wizard-modal-progress-bar";
    WizardModalTestId["STEP_TITLE"] = "wizard-modal-step-title";
    WizardModalTestId["FORWARD_BUTTON"] = "wizard-modal-forward-button";
    WizardModalTestId["BACKWARD_BUTTON"] = "wizard-modal-backward-button";
    WizardModalTestId["SAVE_AND_EXIT_BUTTON"] = "wizard-modal-save-and-exit-button";
    WizardModalTestId["DENY_BUTTON"] = "wizard-modal-deny-button";
    WizardModalTestId["CROSS_BUTTON"] = "wizard-modal-cross-button";
    WizardModalTestId["ERROR_BANNER"] = "wizard-modal-error-banner";
})(WizardModalTestId || (WizardModalTestId = {}));
export const CONTNUE_BUTTON_EVENT = 'continue';
/**
 * Hook for listening modal open and close events
 * @param isOpen
 */
const useEmbedModalEvents = (isOpen) => {
    const { dispatchEmbedEvent } = useEmbedContext();
    React.useEffect(() => {
        if (isOpen) {
            dispatchEmbedEvent(blinkConstants.embedEvents.DialogOpen, {});
        }
        return () => {
            // Dispatch close event only when modal is opened before component unmounts
            if (isOpen) {
                dispatchEmbedEvent(blinkConstants.embedEvents.DialogClose, {});
            }
        };
    }, [dispatchEmbedEvent, isOpen]);
};
export const WizardModal = ({ isOpen, size = ModalSize.LARGE, title = '', titleTooltipConfig, includesHeader = true, includesFooter = true, autoScrollContent = true, isContentLoading, contentLoadingText, denyButton, saveAndExitButtonConfig, hasCrossButton = false, doNotDismissOnOutsideClick = false, doNotDismissOnEsc = false, onDismiss = _.noop, onAfterClose = _.noop, alwaysMaxHeight = true, isContentHeightDynamic = false, noPadding = false, className = '', customHeaderClassName = '', customFooterClassName = '', customLoaderClassName = '', customLoadingTextClassName = '', customPortalClassName = '', customProgressBarClassName = '', customStepsClassName = '', isProgressBarEnabled = false, progressBarColor = ProgressBarColor.BLUE, wizardModalStep = [], currentStep = 0, shouldResetWizardAtEnd = false, wizardError, }) => {
    const { t } = useTranslation();
    const [currentWizardStep, setCurrentWizardStep] = React.useState(currentStep);
    const [embedClass, setEmbedClass] = React.useState({});
    const { dispatchEmbedEvent, embedParams } = useEmbedContext();
    const isFullHeightPinboard = embedParams === null || embedParams === void 0 ? void 0 : embedParams.isFullHeightPinboard;
    const [openModal, setOpenModal] = React.useState(!isFullHeightPinboard && isOpen);
    // Conditionally set page context only when modal is open
    // Use setPageContext directly (not the hook) inside useEffect to avoid Rules of Hooks violation
    React.useLayoutEffect(() => {
        if (openModal && typeof title === 'string' && title.length > 0) {
            const reset = setPageContext({
                page: PageContexts.OTHER,
                pageType: PageType.DIALOG,
                objectIds: {
                    modalTitle: title,
                },
            });
            return () => reset === null || reset === void 0 ? void 0 : reset();
        }
        return undefined;
    }, [openModal, title]);
    useEffect(() => {
        const calculateEmbedModalPadding = async () => {
            try {
                const style = {};
                const embedModalCenter = await dispatchEmbedEvent(blinkConstants.embedEvents.EmbedIframeCenter, {});
                const top = Math.max(0, embedModalCenter.data.iframeScrolled);
                const bottom = embedModalCenter.data.iframeHeight -
                    embedModalCenter.data.iframeVisibleViewPort -
                    top;
                style.paddingTop = top;
                style.paddingBottom = bottom;
                setEmbedClass({ overlay: style });
            }
            catch (error) {
                logger.error(error);
            }
            finally {
                setOpenModal(isOpen);
            }
        };
        if (isFullHeightPinboard && isOpen) {
            calculateEmbedModalPadding();
        }
        else {
            setOpenModal(isOpen);
        }
    }, [dispatchEmbedEvent, isFullHeightPinboard, isOpen]);
    useEffect(() => {
        setCurrentWizardStep(currentStep);
    }, [currentStep]);
    useEmbedModalEvents(isOpen);
    const renderHeader = () => {
        var _a, _b, _c, _d, _e, _f;
        const checkIfWizardPresent = () => {
            return (wizardModalStep.length > 0 &&
                wizardModalStep[currentWizardStep].stepTitle);
        };
        const typographyTitleVariant = checkIfWizardPresent()
            ? 'body-normal'
            : 'modal-title';
        return (React.createElement("div", { className: cx(styles.header, customHeaderClassName, {
                [styles.wizardHeader]: checkIfWizardPresent() && title,
                [styles.hasSaveAndExitBtn]: !!saveAndExitButtonConfig,
            }) },
            React.createElement("div", null,
                React.createElement(Typography, { variant: typographyTitleVariant, ellipsis: { rows: 1 }, className: cx({
                        [styles.headerTitle]: checkIfWizardPresent(),
                    }), tooltipConfig: {
                        showTooltipOnlyOnEllipsis: (_a = titleTooltipConfig === null || titleTooltipConfig === void 0 ? void 0 : titleTooltipConfig.showTooltipOnlyOnEllipsis) !== null && _a !== void 0 ? _a : true,
                        dataTooltipContent: (_b = titleTooltipConfig === null || titleTooltipConfig === void 0 ? void 0 : titleTooltipConfig.dataTooltipContent) !== null && _b !== void 0 ? _b : title,
                    }, noMargin: true }, title),
                checkIfWizardPresent() && (React.createElement(Typography, { variant: "modal-title", ellipsis: { rows: 1 }, tooltipConfig: {
                        showTooltipOnlyOnEllipsis: (_d = (_c = wizardModalStep[currentWizardStep]
                            .stepTitleTooltipConfig) === null || _c === void 0 ? void 0 : _c.showTooltipOnlyOnEllipsis) !== null && _d !== void 0 ? _d : true,
                        dataTooltipContent: ((_e = wizardModalStep[currentWizardStep]
                            .stepTitleTooltipConfig) === null || _e === void 0 ? void 0 : _e.dataTooltipContent) ||
                            ((_f = wizardModalStep[currentWizardStep]) === null || _f === void 0 ? void 0 : _f.stepTitle) ||
                            '',
                    }, noMargin: true, "data-testid": WizardModalTestId.STEP_TITLE }, wizardModalStep[currentWizardStep].stepTitle))),
            saveAndExitButtonConfig && (React.createElement(Button, { type: "tertiary", className: styles.saveAndExitBtn, isDisabled: isContentLoading ||
                    saveAndExitButtonConfig.isDisabled, isSaving: saveAndExitButtonConfig.isLoading, onClick: saveAndExitButtonConfig.onClick, "data-tooltip-content": saveAndExitButtonConfig.tooltip, text: saveAndExitButtonConfig.text, "data-testid": WizardModalTestId.SAVE_AND_EXIT_BUTTON }))));
    };
    const handleContinueBtnClick = (event) => {
        var _a, _b, _c;
        if ((_a = wizardModalStep[currentWizardStep].stepForwardButton) === null || _a === void 0 ? void 0 : _a.onNext) {
            (_c = (_b = wizardModalStep[currentWizardStep].stepForwardButton) === null || _b === void 0 ? void 0 : _b.onNext) === null || _c === void 0 ? void 0 : _c.call(_b, currentWizardStep, () => {
                if (shouldResetWizardAtEnd &&
                    currentWizardStep === wizardModalStep.length - 1) {
                    setCurrentWizardStep(0);
                }
                else {
                    setCurrentWizardStep((prev) => prev + 1);
                }
            }, event);
        }
        else if (shouldResetWizardAtEnd &&
            currentWizardStep === wizardModalStep.length - 1) {
            setCurrentWizardStep(0);
        }
        else if (currentWizardStep < wizardModalStep.length - 1) {
            setCurrentWizardStep((prev) => prev + 1);
        }
        else {
            // Do nothing.
        }
    };
    const renderFooter = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const handleBackBtnClick = () => {
            var _a, _b, _c;
            if ((_a = wizardModalStep[currentWizardStep].stepBackButton) === null || _a === void 0 ? void 0 : _a.onBack) {
                (_c = (_b = wizardModalStep[currentWizardStep].stepBackButton) === null || _b === void 0 ? void 0 : _b.onBack) === null || _c === void 0 ? void 0 : _c.call(_b, currentWizardStep, () => {
                    setCurrentWizardStep((prev) => prev - 1);
                });
            }
            else {
                setCurrentWizardStep((prev) => prev - 1);
            }
        };
        return (React.createElement("div", { className: cx(styles.footer, customFooterClassName) },
            React.createElement("div", { className: styles.denyBtnWrapper }, denyButton && (React.createElement(Button, { type: "tertiary", isDisabled: isContentLoading || denyButton.isDisabled, className: styles.denyBtn, onClick: denyButton.onDeny, text: denyButton.text, "data-tooltip-content": denyButton.tooltip, isSaving: denyButton.isLoading, "data-testid": WizardModalTestId.DENY_BUTTON }))),
            wizardModalStep.length > 0 &&
                wizardModalStep[currentWizardStep].stepBackButton && (React.createElement(Button, { className: styles.actionBtn, type: "secondary", isDisabled: (_a = wizardModalStep[currentWizardStep]
                    .stepBackButton) === null || _a === void 0 ? void 0 : _a.isDisabled, isSaving: (_b = wizardModalStep[currentWizardStep]
                    .stepBackButton) === null || _b === void 0 ? void 0 : _b.isLoading, onClick: handleBackBtnClick, "data-tooltip-content": (_c = wizardModalStep[currentWizardStep]
                    .stepBackButton) === null || _c === void 0 ? void 0 : _c.tooltip, text: (_e = (_d = wizardModalStep[currentWizardStep]
                    .stepBackButton) === null || _d === void 0 ? void 0 : _d.text) !== null && _e !== void 0 ? _e : t('wizardModal.back'), "data-testid": WizardModalTestId.BACKWARD_BUTTON })),
            wizardModalStep.length > 0 &&
                wizardModalStep[currentWizardStep].stepForwardButton && (React.createElement(Button, { className: styles.actionBtn, isDisabled: isContentLoading ||
                    ((_f = wizardModalStep[currentWizardStep]
                        .stepForwardButton) === null || _f === void 0 ? void 0 : _f.isDisabled), isSaving: (_g = wizardModalStep[currentWizardStep]
                    .stepForwardButton) === null || _g === void 0 ? void 0 : _g.isLoading, type: "primary", onClick: handleContinueBtnClick, "data-tooltip-content": (_h = wizardModalStep[currentWizardStep]
                    .stepForwardButton) === null || _h === void 0 ? void 0 : _h.tooltip, text: (_k = (_j = wizardModalStep[currentWizardStep]
                    .stepForwardButton) === null || _j === void 0 ? void 0 : _j.text) !== null && _k !== void 0 ? _k : t('wizardModal.continue'), "data-testid": WizardModalTestId.FORWARD_BUTTON }))));
    };
    useEffect(() => {
        const handler = (event) => {
            handleContinueBtnClick(event);
        };
        document.addEventListener(CONTNUE_BUTTON_EVENT, handler);
        return () => {
            document.removeEventListener(CONTNUE_BUTTON_EVENT, handler);
        };
    });
    const renderError = () => {
        return (wizardError && (React.createElement(Alert.Banner, { message: wizardError.message, isVisible: wizardError.isVisible, type: wizardError.alertType, showCloseButton: wizardError.showCloseButton, onClose: wizardError.onClose || _.noop, primaryAction: wizardError.primaryActionButton, global: true, "data-testid": WizardModalTestId.ERROR_BANNER })));
    };
    const renderSteps = () => {
        const stepClasses = cx(styles.content, customStepsClassName, wizardModalStep[currentWizardStep].stepClassName, {
            [styles.autoScrollContent]: autoScrollContent,
            [styles.noPadding]: noPadding,
            [styles.contentWithHeaderAbove]: title,
            [customLoaderClassName]: isContentLoading,
        });
        return (React.createElement(LoadingIndicator.Global, { hideLoader: !isContentLoading, className: styles.loadingWrapper, loadingTextClassName: customLoadingTextClassName, loadingText: contentLoadingText !== null && contentLoadingText !== void 0 ? contentLoadingText : '' },
            React.createElement("div", { className: stepClasses }, wizardModalStep[currentWizardStep].stepContent)));
    };
    const renderProgressBar = () => {
        const numberOfSteps = wizardModalStep.length > 0 ? wizardModalStep.length : 1;
        const progressSteps = [];
        for (let i = 1; i <= numberOfSteps; i++) {
            progressSteps.push(React.createElement("div", { key: i * 2 - 1, className: cx(styles.progressBarInner, {
                    [styles.redBar]: progressBarColor === ProgressBarColor.RED,
                    [styles.yellowBar]: progressBarColor === ProgressBarColor.YELLOW,
                    [styles.greenBar]: progressBarColor === ProgressBarColor.GREEN,
                    [styles.blueBar]: progressBarColor === ProgressBarColor.BLUE,
                    [styles.grayBar]: i > 1 + currentWizardStep,
                    [styles.firstBar]: i === 1,
                    [styles.lastBar]: i === numberOfSteps,
                }), style: { width: `${100 / numberOfSteps}%` } }));
            if (i < numberOfSteps) {
                progressSteps.push(React.createElement("div", { key: i * 2, className: cx(styles.progressBarSpace) }));
            }
        }
        return (React.createElement("div", { className: cx(customProgressBarClassName, styles.progressBar, {
                [styles.mediumSizeProgressBar]: size === ModalSize.MEDIUM,
            }), "data-testid": WizardModalTestId.PROGRESSBAR }, progressSteps));
    };
    return (React.createElement(ReactModal, { isOpen: openModal, shouldCloseOnOverlayClick: !doNotDismissOnOutsideClick, shouldCloseOnEsc: !doNotDismissOnEsc, closeTimeoutMS: 200, onRequestClose: onDismiss, onAfterClose: onAfterClose, testId: WizardModalTestId.CONTAINER, className: cx(className, styles[size], styles.wizardModal, {
            [styles.alwaysMaxHeight]: alwaysMaxHeight,
            [styles.isContentHeightDynamic]: isContentHeightDynamic,
        }), portalClassName: cx('ReactModalPortal', customPortalClassName), style: isFullHeightPinboard ? embedClass : {} },
        includesHeader && renderHeader(),
        hasCrossButton && (React.createElement(Button, { className: styles.crossBtn, icon: IconID.CROSS, onClick: onDismiss, type: "tertiary", "data-testid": WizardModalTestId.CROSS_BUTTON })),
        renderError(),
        wizardModalStep.length > 0 && renderSteps(),
        isProgressBarEnabled && renderProgressBar(),
        includesFooter && renderFooter()));
};
//# sourceMappingURL=wizard-modal.js.map